import React from 'react';
import { useTheme } from 'styled-components';
import { Spacer } from './Spacer';
import { Poppins } from './Poppins';
import { useNavigate } from 'react-router-dom';
import { GradientText } from './GradientText';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import * as S from './Back.styles';

export const BackSvg = ({ fill }: { fill?: string }) => {
  const colors = useTheme().colors;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
      <path
        d="M20 9.1875H4.7875L11.775 1.85062L10 0L0 10.5L10 21L11.7625 19.1494L4.7875 11.8125H20V9.1875Z"
        fill={fill || colors.prussianBlue}
      />
    </svg>
  );
};

interface BackProps {
  path?: string;
  plain?: boolean;
  bottomSpace?: 0 | 20 | 50;
  fill?: string;
  /**
   * Task 001 (T2.5): when a caller supplies `cb` and no `path`, it is saying it owns the
   * navigation, so the default `navigate(-1)` is skipped. Content Management's detail views need
   * this — their back always returns to the pack list, and `navigate(-1)` on a deep link would
   * leave the app entirely. No existing caller passes `cb`, so nothing else changes.
   */
  cb?: () => void;
}

export const Back: React.FC<BackProps> = ({ path, plain, bottomSpace = 50, fill, cb }) => {
  const colors = useTheme().colors;
  const navigate = useNavigate();

  return (
    <S.Div>
      <div
        className="btn-back"
        data-cy="btn-back"
        onClick={() => {
          if (path) {
            navigate(path);
          } else if (!cb) {
            navigate(-1);
          }
          cb && cb();
          mpEvent(MPEvents.BackArrowClick, {});
        }}
      >
        <div className="back-hit-slop">
          <BackSvg fill={fill || colors.prussianBlue} />
        </div>
        {!plain && (
          <Poppins color="cflowerBlue" px={18} weight={500}>
            Back
          </Poppins>
        )}
      </div>
      <Spacer $px={bottomSpace} />
    </S.Div>
  );
};

interface TitleWithBackProps {
  title: string;
  dataCy?: string;
  gradient?: boolean;
  /** Forwarded to `Back` — see `BackProps.path` / `BackProps.cb`. */
  path?: string;
  cb?: () => void;
}
export const TitleWithBack: React.FC<TitleWithBackProps> = ({ title, dataCy = 'title', gradient, path, cb }) => {
  const colors = useTheme().colors;
  return (
    <S.Twb>
      <Back plain bottomSpace={0} fill={colors.cflowerBlue} path={path} cb={cb} />
      {gradient ? (
        <GradientText data-cy={dataCy}>{title}</GradientText>
      ) : (
        <Poppins px={32} weight={600} data-cy={dataCy}>
          {title}
        </Poppins>
      )}
    </S.Twb>
  );
};
